import React, { useCallback, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router";

import { faSearch } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faCaretDown, faCaretUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import { TextField } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";

import styles from "./SearchFilters.module.css";

interface FilterTriggerProps {
	value?: string;
	triggerContent: string | React.ReactElement;
	isOpen: boolean;
}

export function FilterTrigger({ value, triggerContent, isOpen }: FilterTriggerProps) {
	return (
		<Menu.Trigger>
			<button
				className={cx(styles.filterTrigger, {
					[styles.active]: isOpen,
					[styles.selected]: Boolean(value),
				})}
			>
				{triggerContent}
				<FontAwesomeIcon icon={isOpen ? faCaretUp : faCaretDown} />
			</button>
		</Menu.Trigger>
	);
}

export interface FilterProps {
	triggerContent: FilterTriggerProps["triggerContent"];
	searchParam: string;
	searchParams?: string[];
	onSearch?(query: string): void;
	onClearPressed?(): void;
	hideClearFilter?: boolean;
	searchPlaceholder?: string;
}

export function useClearSearchParam(
	searchParam: FilterProps["searchParam"],
	searchParams?: FilterProps["searchParams"],
) {
	const history = useHistory();
	const location = useLocation();

	return useCallback(() => {
		const search = new URLSearchParams(location.search);
		if (searchParams) {
			searchParams.forEach(param => search.delete(param));
		} else {
			search.delete(searchParam);
		}

		history.replace({
			pathname: location.pathname,
			search: search.toString(),
		});
	}, [location.search, location.pathname, history, searchParam, searchParams]);
}

// TODO: Make this use the SearchableSelector
export default function Filter({
	triggerContent,
	searchParam,
	searchParams,
	onSearch,
	children,
	onClearPressed,
	hideClearFilter,
	searchPlaceholder = "Search users",
}: React.PropsWithChildren<FilterProps>) {
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const toggleOpen = useCallback(() => setIsOpen(isOpen => !isOpen), []);
	const [search, setSearch] = useState<string>("");
	const inputRef = useRef<HTMLInputElement>(null);

	const location = useLocation();

	const value = useMemo(() => {
		const search = new URLSearchParams(location.search);
		if (searchParams) {
			const searchValues = searchParams.map(param => search.get(param));
			return searchValues.find(value => value !== null) ?? undefined;
		}
		return search.get(searchParam) ?? undefined;
	}, [location.search, searchParam, searchParams]);

	// eslint-disable-next-line no-underscore-dangle
	const _clearSearchParam = useClearSearchParam(searchParam, searchParams);
	const clearFilter = useCallback(() => {
		_clearSearchParam();
		onClearPressed?.();
	}, [_clearSearchParam, onClearPressed]);

	const onTextFieldKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
		event.stopPropagation();
	}, []);

	const onInputFocus = useCallback(() => {
		inputRef.current?.focus();
	}, [inputRef]);

	const setValue = useCallback(
		(value: string) => {
			setSearch(value);
			onSearch?.(value);
		},
		[onSearch],
	);

	return (
		<Menu open={isOpen} onOpenChange={toggleOpen}>
			<FilterTrigger value={value} triggerContent={triggerContent} isOpen={isOpen} />
			<Menu.Content onFocus={onInputFocus}>
				{onSearch && (
					<>
						<div className={styles.search}>
							<FontAwesomeIcon icon={faSearch} color="var(--secondaryTextColor)" className="search-icon" />
							<TextField
								value={search}
								setValue={setValue}
								onKeyDown={onTextFieldKeyDown}
								ref={inputRef}
								placeholder={searchPlaceholder}
								autoFocus
								className={styles.searchInput}
							/>
						</div>

						<Menu.Separator />
					</>
				)}
				{children}
				{!hideClearFilter && (
					<>
						<Menu.Separator />
						<Menu.Item
							onSelect={clearFilter}
							className={cx(styles.filter, { [styles.clearFilter]: !!value })}
							disabled={!value}
						>
							Clear filter
						</Menu.Item>
					</>
				)}
			</Menu.Content>
		</Menu>
	);
}
